import { useTheme } from "next-themes";
import { Sun, Moon } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

type ThemeToggleVariant = "pill" | "dropdown" | "icon";

interface ThemeToggleProps {
  variant?: ThemeToggleVariant;
}

export function ThemeToggle({ variant = "pill" }: ThemeToggleProps) {
  const { setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    if (variant === "dropdown") {
      return <div className="h-6 w-full rounded-md bg-muted/50" />;
    }

    if (variant === "icon") {
      return (
        <Button
          variant="ghost"
          size="icon"
          className="text-foreground/80 hover:text-foreground hover:bg-surface/50 shadow-none hover:shadow-none active:shadow-none"
          aria-label="Toggle theme"
        >
          <Sun className="h-5 w-5" />
        </Button>
      );
    }

    return <div className="w-[72px] h-9 bg-muted/50 rounded-full border" />;
  }

  const isDark = resolvedTheme === "dark";

  if (variant === "icon") {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setTheme(isDark ? "light" : "dark")}
        className="text-foreground/80 hover:text-foreground hover:bg-surface/50 shadow-none hover:shadow-none active:shadow-none relative"
        aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
      >
        <motion.div
          initial={false}
          animate={{ rotate: isDark ? 0 : 180 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          {isDark ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </motion.div>
      </Button>
    );
  }

  if (variant === "dropdown") {
    return (
      <div className="flex w-full items-center gap-2">
        {isDark ? (
          <Moon className="h-4 w-4 text-muted-foreground" />
        ) : (
          <Sun className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="text-sm">{isDark ? "Dark" : "Light"}</span>
        <Switch
          checked={isDark}
          onCheckedChange={(checked) => setTheme(checked ? "dark" : "light")}
          aria-label="Toggle theme"
          className="ml-auto"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center p-1 bg-muted/50 rounded-full border relative">
      <button
        onClick={() => setTheme("light")}
        className={cn(
          "flex items-center justify-center w-8 h-8 rounded-full relative z-10 transition-colors duration-200",
          !isDark
            ? "text-foreground"
            : "text-muted-foreground hover:text-foreground"
        )}
        title="Light Mode"
        aria-label="Switch to light mode"
      >
        <Sun className="h-4 w-4" />
        {!isDark && (
          <motion.div
            layoutId="active-theme"
            className="absolute inset-0 bg-background rounded-full shadow-sm border border-border/50 -z-10"
            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
          />
        )}
      </button>
      <button
        onClick={() => setTheme("dark")}
        className={cn(
          "flex items-center justify-center w-8 h-8 rounded-full relative z-10 transition-colors duration-200",
          isDark
            ? "text-foreground"
            : "text-muted-foreground hover:text-foreground"
        )}
        title="Dark Mode"
        aria-label="Switch to dark mode"
      >
        <Moon className="h-4 w-4" />
        {isDark && (
          <motion.div
            layoutId="active-theme"
            className="absolute inset-0 bg-background rounded-full shadow-sm border border-border/50 -z-10"
            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
          />
        )}
      </button>
    </div>
  );
}
